import React, { useCallback, useMemo } from 'react';
import { useSelectedObject } from '@/app/workflows/components/v7/hooks/useSelectedObject';
import {
  CombinatorRendererProps,
  CombinatorSubSchemaRenderInfo,
  createCombinatorRenderInfos,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  OwnPropsOfControl,
} from '@jsonforms/core';
import { JsonFormsDispatch } from '@jsonforms/react';
import { Stack } from '@mantine/core';
import { useNavigationContext } from '../../../contexts/NavigationContext';
import { getDepth, isRequired, isTopLevel } from '../../../utils/navigation';
import CollapsableSection from '../CollapsableSection';
import CombinatorProperties from '../CombinatorProperties';
import { extractOneOfOptionName } from '../../../utils/extractRequiredPaths';

export interface OneOfComponentProps {
  onChange: (value: string | null) => void;
  value: string | null;
  data: string[];
}

export interface OwnOneOfProps extends OwnPropsOfControl {
  indexOfFittingSchema?: number;
}

const getIndex = (data: any, schema: JsonSchema): number => {
  const keys = Object.keys(data ?? {});
  const index =
    schema.oneOf?.findIndex((oneOf) => {
      return oneOf.properties && keys.every((key) => oneOf.properties?.[key]);
    }) ?? 0;
  if (index !== -1) {
    return index;
  }
  return 0;
};

export const MantineOneOfBase = ({
  schema,
  path,
  renderers,
  cells,
  rootSchema,
  id,
  visible,
  uischema,
  uischemas,
  data,
  label,
  description,
  Component,
  componentProps,
  simple = false,
}: CombinatorRendererProps & {
  Component: (props: OneOfComponentProps) => React.ReactNode;
  componentProps?: object;
  simple?: boolean;
}) => {
  const { deleteSelectedObjects, addSelectedObjects } = useSelectedObject();
  const { selectedTopNavigation } = useNavigationContext();

  const depth = getDepth(path);
  const isFirstLevel = isTopLevel(depth);
  const required = isRequired(isFirstLevel, path);

  const selectedIndex = useMemo(() => getIndex(data, schema), [data, schema]);

  const oneOfRenderInfos = useMemo(
    () => createCombinatorRenderInfos((schema as JsonSchema).oneOf!, rootSchema, 'oneOf', uischema, path, uischemas),
    [schema, rootSchema, uischema, path, uischemas]
  );

  const selected = useMemo(() => oneOfRenderInfos[selectedIndex], [oneOfRenderInfos, selectedIndex]);

  const handleSelectChange = useCallback(
    (value: string | null) => {
      const index = oneOfRenderInfos.findIndex((info) => info.label === value);

      const previouslySelectedOneOf = selected;
      const previouslySelectedProperty = extractOneOfOptionName(previouslySelectedOneOf.schema);
      const previouslySelectedPath = `${path}.${previouslySelectedProperty}`;
      const formData = deleteSelectedObjects(previouslySelectedPath);

      const selectedOneOf = oneOfRenderInfos[index];
      const property = extractOneOfOptionName(selectedOneOf.schema);
      addSelectedObjects(`${path}.${property}`, formData);
    },
    [addSelectedObjects, deleteSelectedObjects, oneOfRenderInfos, path, selected]
  );

  if (!visible || (isFirstLevel && selectedTopNavigation !== path)) {
    return null;
  }

  if (simple) {
    return (
      <fieldset disabled={!data}>
        <Component
          {...componentProps}
          data={oneOfRenderInfos.map((info) => info.label)}
          value={selected.label}
          onChange={handleSelectChange}
        />
      </fieldset>
    );
  }

  return (
    <Stack gap='xs'>
      <CombinatorProperties schema={schema} combinatorKeyword={'oneOf'} path={path} rootSchema={rootSchema} />
      <OneOfMemoized
        path={path}
        description={description}
        label={label}
        required={required}
        data={data}
        Component={Component}
        componentProps={componentProps}
        oneOfRenderInfos={oneOfRenderInfos}
        selected={selected}
        handleSelectChange={handleSelectChange}
        renderers={renderers}
        cells={cells}
        selectedIndex={selectedIndex}
      />
    </Stack>
  );
};

const OneOfMemoized = React.memo(function OneOfMemoized({
  path,
  description,
  label,
  required,
  data,
  Component,
  componentProps,
  oneOfRenderInfos,
  selected,
  handleSelectChange,
  renderers,
  cells,
  selectedIndex,
}: {
  path: string;
  description?: string;
  label: string;
  required: boolean;
  data: any;
  Component: (props: OneOfComponentProps) => React.ReactNode;
  componentProps?: object;
  oneOfRenderInfos: CombinatorSubSchemaRenderInfo[];
  selected: CombinatorSubSchemaRenderInfo;
  handleSelectChange: (value: string | null) => void;
  renderers?: JsonFormsRendererRegistryEntry[];
  cells: any;
  selectedIndex: number;
}) {
  return (
    <CollapsableSection
      path={path}
      description={description}
      label={label}
      required={required}
      selected={!!data}
      additionalHeaderContent={
        <fieldset disabled={!data}>
          <Component
            {...componentProps}
            data={oneOfRenderInfos.map((info) => info.label)}
            value={selected.label}
            onChange={handleSelectChange}
          />
        </fieldset>
      }
    >
      {selected && (
        <JsonFormsDispatch
          key={selectedIndex}
          schema={selected.schema}
          uischema={selected.uischema}
          path={path}
          renderers={renderers}
          cells={cells}
        />
      )}
    </CollapsableSection>
  );
});
